import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { MorePlaces, NavHeading, navItemClass, placeClass, tabClass, tabIconClass, tabLabelClass, } from '@kanos/ui';
import { ArrowLeft } from 'lucide-react';
/** Whether a path is the place or lies below it - `/settings` is not `/settings-x`. */
export const within = (pathname, base) => pathname === base || pathname.startsWith(`${base}/`);
/** Whether the place is the one shown: home only at home, every other one with what lies below. */
const isAt = (pathname, place) => place.to === '/'
    ? pathname === '/'
    : [place.to, ...(place.also ?? [])].some((base) => within(pathname, base));
/** At most this many entries in the bar and at the phone's foot (after iOS's tab bar). */
const MAX_PLACES = 5;
/**
 * The places shown as entries and those behind „Mehr“: all of them while they fit, else the
 * first four and „Mehr“ as the fifth - five words fit a phone of 320 px, seven ran into another.
 */
export function splitPlaces(places) {
    if (places.length <= MAX_PLACES)
        return { shown: places, more: [] };
    return { shown: places.slice(0, MAX_PLACES - 1), more: places.slice(MAX_PLACES - 1) };
}
/**
 * The main places as the frame shows them (AppFrame): words in the bar's pill, symbol over word
 * in the phone's bar at the foot; beyond five, the rest behind „Mehr“ (`go` opens one of them).
 * The current one says so (`aria-current`).
 */
export function PlaceLinks({ link: Link, places, pathname, variant, go, }) {
    const { shown, more } = splitPlaces(places);
    const links = shown.map((place) => {
        const active = isAt(pathname, place);
        const Icon = place.icon;
        return (_jsxs(Link, { to: place.to, "aria-current": active ? 'page' : undefined, className: variant === 'bar' ? placeClass(active, place.iconOnly) : tabClass(active), children: [(variant === 'tabs' || place.iconOnly) && (_jsx(Icon, { "aria-hidden": true, className: variant === 'tabs' ? tabIconClass : 'size-5' })), _jsx("span", { className: variant === 'tabs' ? tabLabelClass : place.iconOnly ? 'sr-only' : 'truncate', children: place.label })] }, place.to));
    });
    if (more.length === 0)
        return links;
    return (_jsxs(_Fragment, { children: [links, _jsx(MorePlaces, { variant: variant, places: more.map((place) => ({
                    id: place.to,
                    label: place.label,
                    icon: place.icon,
                    isCurrent: isAt(pathname, place),
                    onAction: () => go(place.to),
                })) })] }));
}
/** An entry of the sidebar; the current place is lifted and announced (`aria-current`). */
export function NavItem({ link: Link, to, label, icon: Icon, active, ariaLabel, }) {
    return (_jsxs(Link, { to: to, "aria-current": active ? 'page' : undefined, "aria-label": ariaLabel, className: navItemClass(active), children: [_jsx(Icon, { "aria-hidden": true, className: "size-4.5 shrink-0" }), _jsx("span", { className: "truncate", children: label })] }));
}
/**
 * The areas of a section (settings, profile) beside its pages, with the way back to its overview
 * on top (after Corly): one list, one lit entry - the place is never in doubt.
 */
export function SectionNav({ link, section, pathname, }) {
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "lg:hidden", children: _jsx(SectionBack, { link: link, section: section }) }), _jsxs("nav", { "aria-label": section.title, className: "grid gap-4 max-lg:hidden", children: [_jsx(SectionBack, { link: link, section: section }), section.groups.map((group) => (_jsxs("div", { role: "group", "aria-label": group.title, children: [_jsx(NavHeading, { children: group.title }), _jsx("div", { className: "grid gap-1", children: group.areas.map((area) => {
                                    const to = `${section.base}/${area.slug}`;
                                    return (_jsx(NavItem, { link: link, to: to, label: area.title, icon: area.icon, active: within(pathname, to) }, area.slug));
                                }) })] }, group.key)))] })] }));
}
/** The way from an area of a section back to its overview. */
function SectionBack({ link, section }) {
    return (_jsx(NavItem, { link: link, to: section.base, label: section.title, icon: ArrowLeft, active: false, ariaLabel: `Zur Übersicht: ${section.title}` }));
}
//# sourceMappingURL=navigation.js.map